/* Guitar World native search presentation. Shopify remains the search provider. */
class GWSearch extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.events = new AbortController();
    this.input = this.querySelector('input[name="q"]');
    this.panel = this.querySelector('.gw-search__panel');
    this.content = this.querySelector('.gw-search__content');
    this.status = this.querySelector('.gw-search__state');
    this.clearButton = this.querySelector('.gw-search__clear');
    this.viewAll = this.querySelector('.gw-search__view-all');
    this.initial = this.querySelector('template');
    this.mobile = this.classList.contains('gw-search--mobile');
    this.cache = new Map(); this.version = 0;
    const options = { signal: this.events.signal };
    this.input.addEventListener('focus', () => { if (!this.suppressFocus) this.open(); }, options);
    this.input.addEventListener('input', () => this.search(), options);
    this.clearButton.addEventListener('click', () => { this.input.value = ''; this.search(); this.input.focus(); }, options);
    this.querySelector('.gw-search__close').addEventListener('click', () => this.close(true), options);
    this.addEventListener('keydown', (event) => this.keydown(event), options);
    this.addEventListener('focusout', (event) => { if (!this.mobile && event.relatedTarget && !this.contains(event.relatedTarget)) this.close(); }, options);
    document.addEventListener('pointerdown', (event) => { if (!this.mobile && !event.composedPath().includes(this)) this.close(); }, options);
    document.addEventListener('dialog:open', (event) => { if (!this.contains(event.target) && !event.target.contains(this)) this.close(); }, { ...options, capture: true });
    document.addEventListener('theme-drawer:open', () => this.close(), options);
    this.closest('dialog-component')?.addEventListener('dialog:open', () => { this.open(); this.input.focus(); }, options);
    this.closest('dialog')?.addEventListener('close', () => this.close(), options);
    window.addEventListener('resize', () => this.position(), options);
    this.position();
  }
  disconnectedCallback() { this.events?.abort(); this.invalidate(); }
  invalidate() { clearTimeout(this.timer); this.request?.abort(); this.version++; }
  position() {
    if (!this.mobile) {
      const header = this.closest('header-component');
      const edge = (header?.querySelector('.header__row--top') ?? header ?? this.querySelector('form')).getBoundingClientRect();
      const origin = header?.getBoundingClientRect() ?? { top: 0, left: 0 };
      const top = edge.bottom - origin.top;
      const content = header?.querySelector('.header__row--top .header__columns')?.getBoundingClientRect() ?? edge;
      this.style.setProperty('--gw-search-left', `${content.left - origin.left}px`);
      this.style.setProperty('--gw-search-width', `${content.width}px`);
      this.style.setProperty('--gw-search-top', `${top}px`);
      this.style.setProperty('--gw-search-max-height', `${Math.max(140, innerHeight - edge.bottom - 20)}px`);
    }
  }
  open() {
    if (!this.mobile && innerWidth < 990) return;
    if (!this.panel.hidden) return;
    this.position(); requestAnimationFrame(() => { if (this.isConnected) this.position(); }); this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true');
    if (!this.mobile) this.dispatchEvent(new CustomEvent('dialog:open'));
    if (this.input.value.trim()) this.search(); else this.restore();
  }
  close(focus = false) {
    this.invalidate(); this.panel.hidden = true; this.input.setAttribute('aria-expanded', 'false');
    this.content.inert = false; this.panel.removeAttribute('aria-busy');
    if (focus && this.mobile) this.closest('dialog-component')?.closeDialog();
    else if (focus) { this.suppressFocus = true; this.input.focus(); this.suppressFocus = false; }
  }
  restore() {
    this.content.replaceChildren(this.initial.content.cloneNode(true)); this.content.inert = false;
    this.panel.removeAttribute('aria-busy'); this.status.textContent = ''; this.viewAll.hidden = true; this.clearButton.hidden = true;
  }
  search() {
    this.invalidate(); const query = this.input.value.trim();
    this.panel.hidden = false; this.input.setAttribute('aria-expanded', 'true');
    this.clearButton.hidden = !query;
    if (!query) { this.restore(); return; }
    this.panel.setAttribute('aria-busy', 'true'); this.content.inert = true; this.status.textContent = 'Searching…';
    this.viewAll.hidden = false; this.viewAll.textContent = 'View all products'; this.viewAll.href = this.resultsURL(query);
    const version = this.version;
    this.timer = setTimeout(() => this.load(query, version), 200);
  }
  resultsURL(query) {
    const url = new URL(this.dataset.searchUrl, location.origin);
    url.searchParams.set('q', query); url.searchParams.set('type', 'product'); url.searchParams.set('options[prefix]', 'last');
    return url.href;
  }
  async load(query, version) {
    if (version !== this.version) return;
    const request = this.request = new AbortController();
    try {
      let html = this.cache.get(query)?.html;
      if (!html) {
        const url = new URL(this.dataset.suggestUrl, location.origin);
        url.searchParams.set('q', query); url.searchParams.set('resources[limit_scope]', 'each'); url.searchParams.set('section_id', 'gw-search-results');
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Search response unavailable');
        html = await response.text();
      }
      if (version !== this.version || this.panel.hidden) return;
      const documentResult = new DOMParser().parseFromString(html, 'text/html');
      const result = documentResult.querySelector('[data-gw-results]');
      if (!result || result.dataset.query !== query) throw new Error('Search response mismatch');
      this.content.replaceChildren(result); this.content.inert = false; this.panel.removeAttribute('aria-busy');
      const links = result.querySelectorAll('a').length;
      this.status.textContent = links ? `Suggestions for “${query}”` : `No results found for “${query}”`;
      const entry = this.cache.get(query) || {}; entry.html = html; this.cache.set(query, entry);
      if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
      if (this.dataset.count === 'true') this.count(query, version, request.signal);
    } catch (error) {
      if (error.name === 'AbortError' || version !== this.version) return;
      this.content.replaceChildren(); this.content.inert = false; this.panel.removeAttribute('aria-busy');
      this.status.textContent = 'Suggestions are unavailable. Use View all products or press Enter to search.';
    }
  }
  async count(query, version, signal) {
    try {
      let total = this.cache.get(query)?.count;
      if (total === undefined) {
        const url = new URL(this.resultsURL(query)); url.searchParams.set('section_id', 'gw-search-count');
        const response = await fetch(url, { signal }); if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const value = doc.querySelector('[data-gw-search-count]')?.dataset.gwSearchCount;
        if (!value || !/^\d+$/.test(value)) return;
        total = Number(value); const entry = this.cache.get(query); if (entry) entry.count = total;
      }
      if (version === this.version && !this.panel.hidden) this.viewAll.textContent = `View all products (${total.toLocaleString()})`;
    } catch { /* Product suggestions and normal search remain usable if count fails. */ }
  }
  keydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.panel.hidden || this.content.inert) return;
    const links = [...this.content.querySelectorAll('a')].filter(link => link.getClientRects().length);
    if (!links.length) return;
    event.preventDefault(); const index = links.indexOf(document.activeElement);
    if (event.key === 'ArrowUp' && index <= 0) this.input.focus();
    else links[(index + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length].focus();
  }
}
if (!customElements.get('gw-search')) customElements.define('gw-search', GWSearch);
document.addEventListener('keydown', async (event) => {
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
  event.preventDefault();
  await Promise.allSettled([...document.querySelectorAll('dialog[open]')].map(dialog => new Promise(resolve => {
    const done = () => { clearTimeout(timer); dialog.removeEventListener('close', done); resolve(); };
    const timer = setTimeout(done, 1000);
    dialog.addEventListener('close', done, { once: true });
    const owner = dialog.closest('dialog-component, theme-drawer, quick-add-dialog, gw-category-navigation, gw-enquiry-widget');
    owner?.closeDialog?.() ?? owner?.close?.(false);
    if (!dialog.open) done();
  })));
  if (innerWidth >= 990) document.querySelector('.gw-search--desktop input')?.focus();
  else document.querySelector('#search-modal')?.showDialog?.();
});
const searchWidth = matchMedia('(min-width: 990px)');
searchWidth.addEventListener('change', async () => {
  const desktop = document.querySelector('.gw-search--desktop'); const mobile = document.querySelector('.gw-search--mobile');
  if (!desktop || !mobile) return;
  const from = searchWidth.matches ? mobile : desktop; const to = searchWidth.matches ? desktop : mobile;
  const wasOpen = !from.panel.hidden;
  to.input.value = from.input.value; from.close();
  if (searchWidth.matches) { await mobile.closest('dialog-component')?.closeDialog(); if (wasOpen) to.input.focus(); }
  else if (wasOpen) mobile.closest('dialog-component')?.showDialog();
});
